import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api, getHeaders } from '@/lib/api';
import { generateCourseEmbed } from '@/lib/courseEmbed';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GraduationCap, Download, Code2, Search, Star, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = iso => iso ? new Date(iso).toLocaleString('es-CO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

const Stat = ({ label, value, testid, active, onClick }) => (
  <button onClick={onClick} data-testid={testid}
    className={`text-left p-4 rounded-2xl border transition-colors ${active ? 'border-[#A38560] bg-[#A38560]/10' : 'border-white/5 bg-[#0A1F1A] hover:border-white/15'}`}>
    <p className="text-[10px] uppercase tracking-wider text-white/40">{label}</p>
    <p className="font-outfit text-2xl font-semibold text-white tabular-nums mt-1">{value ?? 0}</p>
  </button>
);

const CodesDialog = ({ open, onClose, forms, ty }) => {
  const copy = t => navigator.clipboard.writeText(t).then(() => toast.success('Código copiado'));
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="course-codes-dialog" className="bg-[#0A1F1A] border-[#1A3B32] text-white w-[calc(100%-1.5rem)] max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader><DialogTitle className="font-outfit">Códigos para la landing</DialogTitle></DialogHeader>
        <p className="text-xs text-white/50">Después de enviar, la persona va a la página de gracias de cliente potencial{ty ? `: ${ty}` : ' (no configurada: se muestra un mensaje de gracias)'}.</p>
        {forms.map(f => {
          const code = generateCourseEmbed(f, API_URL, (ty || '').replace(/'/g, '%27'));
          return (
            <div key={f.id} className="space-y-2" data-testid={`course-code-${f.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium">{f.name}</p>
                  <p className="text-[11px] text-white/40">form_id: <code>{f.id}</code> · source: <code>{f.source}</code> · assignmentCode: <code>{f.assignment_code}</code></p>
                </div>
                <button onClick={() => copy(code)} data-testid={`course-code-copy-${f.id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-[#A38560] text-[#03110D] font-semibold shrink-0"><Copy size={12} />Copiar</button>
              </div>
              <pre className="text-[10px] leading-snug bg-black/40 border border-white/5 rounded-xl p-3 max-h-40 overflow-auto whitespace-pre-wrap break-all text-white/60">{code}</pre>
            </div>
          );
        })}
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ l, forms }) => (
  <tr data-testid={`course-lead-${l.id}`} className="border-t border-white/5 align-top">
    <td className="py-3 pr-3">
      <p className="text-white flex items-center gap-1.5">{l.name || <span className="text-white/30">Sin nombre</span>}
        {l.priority && <span data-testid={`course-priority-${l.id}`} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#A38560] text-[#03110D] font-bold flex items-center gap-0.5"><Star size={9} />PRIORIDAD</span>}</p>
      <p className="text-xs text-white/50 break-all">{l.email}</p>
    </td>
    <td className="py-3 px-3 text-white/70 whitespace-nowrap">{l.phone || '—'}</td>
    <td className="py-3 px-3 text-white/70">{l.years_as_agent || '—'}</td>
    <td className="py-3 px-3 text-white/70">{l.has_instagram || '—'}</td>
    <td className="py-3 px-3"><div className="flex flex-wrap gap-1">{(l.forms || []).map(f => <span key={f} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-white/70">{forms.find(x => x.id === f)?.name || f}</span>)}</div></td>
    <td className="py-3 pl-3 text-xs text-white/40 whitespace-nowrap">{fmt(l.updated_at)}</td>
  </tr>
);

export default function CourseLeads() {
  const { user } = useAuth();
  if (user && !['admin', 'deeplook'].includes(user.role)) return <Navigate to="/" replace />;
  return <CourseLeadsView />;
}

function CourseLeadsView() {
  const [data, setData] = useState({ items: [], stats: {}, forms: [] });
  const [filter, setFilter] = useState({ form: '', priority: '' });
  const [q, setQ] = useState('');
  const [codes, setCodes] = useState(false);
  const [ty, setTy] = useState('');
  const qs = new URLSearchParams({ ...filter, q }).toString();

  const load = useCallback(() => api.get(`/course-leads?${qs}`).then(setData).catch(() => toast.error('No se pudieron cargar los leads')), [qs]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get('/settings/thank-you-pages').then(d => setTy(d.thank_you_url_potencial || '')).catch(() => {}); }, []);

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/course-leads/export?${qs}`, { headers: getHeaders(), responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = 'leads_curso.csv'; a.click();
    } catch { toast.error('No se pudo descargar'); }
  };
  const setF = patch => setFilter(f => ({ ...f, ...patch }));
  const s = data.stats;

  return (
    <div data-testid="course-leads-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-outfit text-2xl font-semibold text-white flex items-center gap-2"><GraduationCap className="text-[#A38560]" />Leads Curso</h1>
        <div className="flex gap-2">
          <button onClick={() => setCodes(true)} data-testid="course-codes-btn" className="flex items-center gap-1.5 px-4 h-10 rounded-xl text-sm border border-white/10 text-white/80 hover:text-white"><Code2 size={14} />Códigos</button>
          <button onClick={exportCsv} data-testid="course-export-btn" className="flex items-center gap-1.5 px-4 h-10 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D]"><Download size={14} />Excel</button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Total" value={s.total} testid="course-stat-total" active={!filter.form && !filter.priority} onClick={() => setF({ form: '', priority: '' })} />
        {data.forms.map(f => <Stat key={f.id} label={f.name} value={s[f.id]} testid={`course-stat-${f.id}`} active={filter.form === f.id} onClick={() => setF({ form: filter.form === f.id ? '' : f.id })} />)}
        <Stat label="PRIORIDAD (ambos)" value={s.priority} testid="course-stat-priority" active={filter.priority === 'true'} onClick={() => setF({ priority: filter.priority ? '' : 'true' })} />
      </div>
      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <Input data-testid="course-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre, correo o celular" className="bg-[#0A1F1A] border-white/10 text-white h-10 rounded-xl pl-9" />
      </div>
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="text-[10px] uppercase tracking-wider text-white/40 text-left">
            <th className="py-2 pr-3">Persona</th><th className="py-2 px-3">Celular</th><th className="py-2 px-3">Tiempo como agente</th>
            <th className="py-2 px-3">Instagram inmobiliario</th><th className="py-2 px-3">Formularios</th><th className="py-2 pl-3">Último registro</th></tr></thead>
          <tbody>{data.items.map(l => <Row key={l.id} l={l} forms={data.forms} />)}</tbody>
        </table>
        {data.items.length === 0 && <p data-testid="course-empty" className="text-sm text-white/40 text-center py-10">Aún no hay leads con estos filtros.</p>}
      </div>
      <CodesDialog open={codes} onClose={() => setCodes(false)} forms={data.forms} ty={ty} />
    </div>
  );
}
